
import React from 'react';
import { motion } from 'framer-motion';
import { LucideIcon } from 'lucide-react';

interface ValueCardProps {
  card: {
    id: number;
    title: string;
    description: string;
    icon: LucideIcon;
    gradient: string;
    glowColor: string;
    borderColor: string;
    delay: number;
  };
}

const ValueCard = ({ card }: ValueCardProps) => {
  const IconComponent = card.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 40, scale: 0.9 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ 
        duration: 0.6,
        delay: card.delay,
        type: "spring",
        damping: 20,
        stiffness: 100
      }}
      viewport={{ once: true }}
      whileHover={{ 
        y: -8,
        scale: 1.02,
        transition: { duration: 0.3 }
      }}
      className="group relative"
    >
      <div className={`
        relative p-6 rounded-2xl
        bg-gradient-to-br ${card.gradient}
        backdrop-blur-xl border ${card.borderColor}
        shadow-2xl hover:shadow-3xl
        transition-all duration-500
        before:absolute before:inset-0 before:rounded-2xl 
        before:bg-gradient-to-br before:${card.gradient}
        before:opacity-0 hover:before:opacity-50
        before:transition-opacity before:duration-300
        before:-z-10
      `}>
        
        <div className={`
          absolute -inset-1 bg-gradient-to-r 
          from-${card.glowColor}/30 via-${card.glowColor}/20 to-${card.glowColor}/30 
          rounded-2xl blur-lg opacity-0 group-hover:opacity-100 
          transition-opacity duration-500 -z-20
        `} />

        <motion.div 
          className="flex justify-center mb-4"
          whileHover={{ scale: 1.1, rotate: 5 }}
          transition={{ duration: 0.3 }}
        >
          <div className={`
            p-3 rounded-xl 
            bg-gradient-to-br from-white/25 to-white/10
            backdrop-blur-sm border ${card.borderColor}
            shadow-xl
          `}>
            <IconComponent className="w-6 h-6 text-orange-300" />
          </div>
        </motion.div>
        
        <h3 className="text-lg font-bold text-white mb-3 text-center">
          {card.title}
        </h3>
        
        <p className="text-gray-200 leading-relaxed text-sm text-center">
          {card.description}
        </p>

        <div className={`
          mt-4 mx-auto w-12 h-0.5 
          bg-gradient-to-r from-${card.glowColor}/60 via-${card.glowColor}/40 to-transparent
          rounded-full shadow-sm
        `} />
      </div>
    </motion.div>
  );
};

export default ValueCard;
